<script>
  import { mapActions, mapGetters } from 'vuex';
  import rules from '@/helpers/validation-rules';
  import TopNav from '@/components/appBar/TopNav.vue';
  import BottomNav from '@/components/appBar/BottomNav.vue';
  import { CURRENCIES } from '@/store';
  import breakpoints from '@/mixins/breakpoints';
  import { Navigation } from '@/mixins/navigation';
  import InjectAppBar from '@/layouts/vertical/InjectAppBar.vue';

  export default {
    name: 'VerticalAppBar',

    mixins: [breakpoints, Navigation],

    components: {
      InjectAppBar,
      NotificationDrawer: () => import('../common-drawer/NotificationDrawer.vue'),
      BottomNav,
      TopNav,
    },

    emits: ['mounted'],

    data() {
      return {
        rules,
        email: '',
        show: false,

        password: '',
        model: {
          payment_currency: '',
          sendaddress: '',
          amount: '',
        },
        alertDrawer: false,
        signInDiag: false,
        currency: CURRENCIES.MMC,
        notificationDrawer: false,

        mqc: 0.0,
        mmc: 0.0,
        isValid: false,
        showDialog: false,
        dialogTitle: '',
        dialogMessage: '',
        drawer: false,
      };
    },

    computed: {
      ...mapGetters(['getSideBarState', 'getThemeMode']),

      currencies() {
        return this.$store.state.currencies;
      },

      appBarHeight() {
        return 82;
      },

      notificationDrawerStyle() {
        return `margin-top: ${this.appBarHeight}px;`;
      },

      promosAmount() {
        return this.$store.getters.getPromoPackages.length;
      },

      playerMode() {
        return this.$store.state.playermode;
      },

      gameWindow() {
        return this.$store.state.gameWindow;
      },

      userSuspended() {
        return this.$store.getters.isUserSuspended;
      },

      showAlerts() {
        if (this.$store.getters.alerts && this.isLoggedInUser) {
          const activeAlerts = this.$store.getters.alerts.filter((alert) => !alert.deleted);
          return activeAlerts.length > 0;
        }

        return false;
      },

      userInfo() {
        return this.$store.state.user;
      },

      isLoggedInUser() {
        return !!this.userInfo;
      },

      userDetails() {
        return this.userInfo?.displayName || this.userInfo || '';
      },
    },

    created() {
      const savedCurrency = localStorage.getItem('currency');
      if (savedCurrency) {
        this.currency = savedCurrency;
      }
    },

    mounted() {
      this.$emit('mounted');
    },

    watch: {
      showAlerts(val) {
        if (!val) return;

        const newNotification = this.$store.state.newNotification;

        if (newNotification) {
          this.dialogTitle = newNotification.type;
          this.dialogMessage = newNotification.message;
          this.showDialog = true;

          setTimeout(() => {
            this.showDialog = false;
            this.$store.dispatch('removeNewNotification');
          }, 5000);
        }
      },

      playerMode(newMode) {
        this.currency = newMode;
      },
      // Watch for changes in currency and update the store's playerMode
      currency(newCurrency) {
        this.$store.commit('setPlayerMode', newCurrency);
      },

      getSideBarState() {
        this.drawer = this.getSideBarState;
      },

      '$store.state.totalBalance'(val) {
        this.mqc = Number(
          (parseFloat(val?.balance?.MQC || 0) / 100).toString().match(/^-?\d+(?:\.\d{0,2})?/)[0],
        ).toLocaleString();
        this.mmc = Number(
          (parseFloat(val?.balance?.MMC || 0) / 100).toString().match(/^-?\d+(?:\.\d{0,2})?/)[0],
        ).toLocaleString();
      },
    },

    methods: {
      ...mapActions(['changeVerticalSidebarDrawer', 'toggleSidebar', 'changeVerticalSidebarMini', 'signOut']),

      openSignUpDialog() {
        this.$store.dispatch('changeSignupDialogState', true);
      },

      toggleVerticalSidebarDrawer() {
        this.changeVerticalSidebarDrawer();
        this.toggleSidebar(true);
      },
    },
  };
</script>

<template>
  <div class="ma-0 pa-0">
    <InjectAppBar />

    <v-app-bar
      :color="$vuetify.theme.dark ? 'dark' : getThemeMode.appBarColor"
      :dark="getThemeMode.appBarColor !== 'white'"
      :height="appBarHeight"
      app
      class="d-block px-sm text-left pa-0 ma-0"
      clipped-right
      flat
    >
      <!--
      <DialogWallet v-if="userDetails && !userSuspended" :show-vault-button="false" />
      -->

      <TopNav
        :is-logged-in-user="isLoggedInUser"
        :notification-drawer="notificationDrawer"
        :show-alerts="showAlerts"
        @notification-toggle="notificationDrawer = !notificationDrawer"
        @sidebar-toggle="toggleVerticalSidebarDrawer"
      />

      <v-progress-linear
        :active="getThemeMode.isLoading"
        :indeterminate="getThemeMode.isLoading"
        absolute
        bottom
        color="primary"
      />
    </v-app-bar>

    <v-navigation-drawer
      v-model="notificationDrawer"
      :style="notificationDrawerStyle"
      :width="$vuetify.breakpoint.xs ? '100%' : 500"
      class="notification-drawer"
      dark
      fixed
      floating
      height="100%"
      overlay-opacity="0"
      right
      temporary
    >
      <notification-drawer>
        <template v-slot:notificationDrawerCloseButton>
          <v-btn color="#F8BF1E" icon @click.stop="notificationDrawer = !notificationDrawer">
            <v-icon size="28">mdi-close</v-icon>
          </v-btn>
        </template>
      </notification-drawer>
    </v-navigation-drawer>

    <BottomNav v-if="$vuetify.breakpoint.smAndDown && isLoggedInUser && !gameWindow" />
  </div>
</template>

<style lang="scss">
  @keyframes flip {
    from {
      transform: rotateY(0deg) translate(0, -49%);
    }
    to {
      transform: rotateY(360deg) translate(0, -49%);
    }
  }

  .sign-up-btn {
    span {
      color: black !important;
    }
  }

  .navbar-tooltip {
    min-width: 100px !important;
  }

  .refer-now-btn {
    min-width: 90px !important;

    @media screen and (max-width: 768px) {
      width: 120px !important;
      min-width: 120px !important;
      margin: 0 !important;
      min-height: 33px !important;

      &.top-bar {
        display: none;
      }
    }
  }

  .v-toolbar__content {
    max-width: 1448px;
    margin: 0;
    margin-inline: auto;
    padding-right: 16px;
  }

  .header-row-1 {
    margin: 0 -16px !important;
    background-color: #24073d;
    container-name: header;
    container-type: inline-size;
  }

  @media screen and (max-width: 1300px) {
    .header-coins {
      justify-content: flex-start !important;
    }
  }

  .mobile-header-row-2 {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #000c;
    margin: 0 -16px !important;
    font-size: 20px;
    font-weight: 700;

    &:not(.is-logout) {
      justify-content: center;
    }

    @media screen and (min-width: 1660px) {
      display: none !important;
    }

    @media screen and (min-width: 993px) {
      &.is-logout {
        display: none !important;
      }
    }
  }

  .header-row-2-text {
    max-width: calc(100% - 64px);
  }

  .header-badge .v-badge__badge.red {
    position: absolute;
    top: -7px !important;
    left: 3px !important;
    bottom: auto !important;
    transform: scale(0.4);
  }

  .header-badge.no-dot .v-badge__badge.red {
    display: none;
  }

  .header-friends-button {
    border-radius: 7px 0 0 7px;
  }

  .header-add-friends-button {
    border-radius: 0 7px 7px 0;
  }

  .header-badge .v-badge__badge.red::after {
    content: '';
    border: 2px solid #191919;
    background-color: red;
  }

  .header-coins {
    position: relative;
    height: 100%;
    align-items: center;
    justify-content: center;

    .header-coins-body {
      position: relative;
      max-width: 200px;
      width: 100%;
      display: flex;
      justify-content: flex-end;
      align-items: center;

      .toggle-background {
        position: absolute;
        inset: 0;
        bottom: -1px;
        background: #0a0a0a;
        pointer-events: none;
        margin: 5px 0;
        border: 1px solid rgba(255, 255, 255, 0.2);
        box-shadow: -2px -2px 2px 0 inset rgba(255, 255, 255, 0.1);
        transform: translateY(-1px);
        border-radius: 18px;
      }

      .balance-coin {
        animation: flip 0.3s ease;
        position: absolute;
        width: 27px;
        height: 27px;
        left: 6px;
        top: 50%;
        transform: translate(0, -49%);
        border-radius: 50%;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
        padding: 1px;
      }

      .coins-amount {
        position: absolute;
        left: 40px;
        overflow: hidden; /* Hide overflowing content */
        white-space: nowrap; /* Prevent text from wrapping */
        text-overflow: ellipsis;
        max-width: 110px;
      }
    }
  }

  .header-coins span {
    font-size: 20px;
    font-weight: 800;
  }

  .header-toggle .v-input__slot {
    margin-bottom: 0;
  }

  .header-toggle .v-input--switch__thumb {
    box-shadow:
      0 1px 3px 4px rgba(0, 0, 0, 0.5),
      0 4px 5px 0 rgba(0, 0, 0, 0.14),
      0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
    background-image: url('/images/figures/mc-coin.webp?v=1');
    background-size: cover;
    background-position: center;
    background-color: transparent;
    height: 20px;
    width: 20px;
    border-radius: 12px;
    position: relative;

    &.white--text {
      background-image: url('/images/figures/qc-coin.png');
    }
  }

  .header-toggle .v-input--selection-controls__ripple {
    display: none;
  }

  .header-toggle .v-input--switch__track {
    background-color: white;
    opacity: 1 !important;
    width: 32px;
  }

  .header-toggle .v-messages {
    display: none;
  }

  .header-logo {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 0;

    .beta-logo {
      margin-left: -15px;

      &--more-margin {
        margin-left: -20px;
      }
    }
  }

  .notification-drawer {
    background: transparent;
    box-shadow: none;
    height: 100% !important;
    z-index: 202 !important;

    @media screen and (max-width: 768px) {
      margin-top: 0 !important;
    }

    .v-navigation-drawer__content {
      background: #24073d;
    }
  }

  .tippy-box[data-theme~='light'] {
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .vnb {
    background: transparent !important;

    &__menu-options {
      margin-top: 3px !important;

      &__option {
        &__link {
          &:focus {
            outline: none;
            border: 0;
          }

          &:hover {
            color: #0081ff;

            .vnb__menu-options__option__arrow {
              fill: #0081ff;
            }
          }

          &__icon {
            svg {
              fill: #0081ff !important;
            }
          }
        }
      }
    }

    &__sub-menu-options {
      &__option {
        &__link {
          &:focus {
            outline: none;
            border: 0;
          }

          color: #000 !important;

          &:hover {
            color: #0081ff !important;
          }
        }
      }
    }
  }

  .vnb__collapse-button {
    &:focus {
      border: 0;
      outline: none;
    }

    &:after {
      content: '\F035C';
      font-size: 25px;
      font-weight: 600;
      font-family: 'Material Design Icons', serif;
    }

    svg {
      display: none !important;
    }
  }

  .vnb__popup {
    max-height: 80vh;
    overflow-x: hidden;
    overflow-y: scroll !important;

    .vnb__popup__top__close-button {
      &:focus {
        border: 0;
        outline: none;
      }

      top: 20px;
      right: -33px;

      svg {
        fill: #000 !important;
      }
    }
  }

  .header-text {
    font-size: 24px;
    color: #fff;
    font-weight: 700;
    text-align: center;
    line-height: 1.1;
  }

  .walletTabs .tab_section {
    background-color: #432459;
    padding: 48px;
    min-height: 220px;
  }

  .wallet-tab {
    background-color: #8779b6;
  }

  .wallet-tab.v-tab--active {
    background-color: #432459;
  }

  .wallet-tab::before {
    content: none;
  }

  .bonus-input {
    position: relative;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;

    .v-input__slot {
      border-radius: 0 !important;
    }

    &::before {
      content: '';
      position: absolute;
      z-index: 2;
      top: 0;
      height: 50px;
      width: 20px;
      left: -19px;
      background-size: auto 100%;
      background-image: url('/images/cards/BonusCode.png');
    }
  }

  .bonus-button {
    box-sizing: border-box;
    background-color: #f8bf1e !important;
    color: #fff !important;
    border: none;
    outline: none;
    border-radius: 0 10px 10px 0;
    padding: 12px 48px !important;
    font-size: 20px !important;
    font-weight: 600 !important;
    height: 48px !important;
    position: relative;
  }

  .bonus-button::after {
    content: '';
    position: absolute;
    top: -6px;
    left: -7px;
    bottom: -6px;
    border-left: 2px dotted #fff;
  }

  .walletDialog {
    background-color: rgb(67, 36, 89) !important;
  }

  .walletDialog .close-button-white {
    position: absolute;
    top: 8px;
    right: 0;
    color: #fff;
    font-size: 40px;
  }

  .walletDialog .v-tabs-items {
    background-color: rgb(67, 36, 89);
  }

  .special-offer-timer {
    padding: 16px 48px;
    border-radius: 12px;
    background-color: #583972;
    max-width: fit-content;
    margin: 16px auto;
    box-shadow:
      4px 4px 16px #000,
      inset 2px 2px 0 #9287b3;
    display: flex;
    justify-content: space-between;
  }

  .timer-number {
    font-size: 40px;
    text-align: center;
    color: #aea8d3;
  }

  .timer-text {
    text-align: center;
    font-size: 1rem;
    padding-top: 8px;
    color: #aea8d3;
    font-weight: 500;
  }

  .withdrawalSlider .v-slide-group__content {
    justify-content: center;
    width: 100%;
  }

  .withdrawalSliderContent .v-subheader.text-h6 {
    width: 20%;
    justify-content: right;
  }

  .withdrawalSliderContent .v-subheader {
    color: #fff;
    font-weight: 600;
  }

  .withdrawal-grid-container {
    display: grid;
    grid-template-columns: auto auto auto;

    .withdrawalInput {
      grid-column-start: 2;
      grid-column-end: 4;
    }

    .withdrawalInput.amount {
      grid-column-start: 2;
      grid-column-end: 3 !important;
    }
  }

  .withdrawalSlider .withdawalSlide {
    padding: 5%;
    width: 20%;
    position: relative;
    transition: all 0.4s ease;
  }

  .withdrawalSlider .v-slide-item--active::before {
    background-image: url('/images/icons/IconSelected_Glow.png');
    background-position: center center;
    background-size: contain;
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }

  .withdrawalSlider .v-slide-item--active {
    transform: scale(1.2);
  }

  .withdrawalSlider .withdawalSlide .inactive {
    opacity: 0.5;
  }

  .withdrawalSlider .withdawalSlide:hover .inactive {
    opacity: 0.8;
    transition: all 0.4s ease;
  }

  .v-input.withdrawalInput {
    padding: 10px;

    .v-input__control {
      .v-input__slot {
        min-height: 36px !important;
        border-radius: 10px !important;
        background: #fff;
      }
    }
  }

  .withdrawalInput .v-input__control .v-input__slot:before,
  .withdrawalInput .v-input__control .v-input__slot:after {
    content: none;
  }

  .header-buttons {
    justify-content: flex-end;
    align-items: center;
    flex: 0;

    .login {
      border: 1px solid #f8bf1e;
    }

    .join {
      &:after {
        content: none !important;
      }
    }
  }

  .header-row-2-logo {
    display: flex;
    flex-direction: row;
    align-items: center;

    .beta-logo {
      margin-left: -20px;
    }
  }

  @media only screen and (max-width: 1300px) {
    .header-text {
      font-size: 16px;
    }

    .header-logo.is-login {
      max-width: 20%;
      height: auto;
    }
  }

  .withdrawalSliderMobile {
    display: none;
  }

  @media screen and (max-width: 1150px) {
    .header-logo.is-login {
      @media screen and (max-width: 993px) {
        display: none;
      }

      .mq-logo {
        display: none;
      }
    }
  }

  @media only screen and (max-width: 1080px) {
    .header-coins {
      span {
        font-size: 16px;
        max-width: 60px;
        text-align: center;
      }

      .header-coins-icon {
        left: 0;

        &.MMC {
          right: 6px;
        }
      }
    }

    .header-toggle .v-input--selection-controls__input {
      width: 48px !important;
    }

    .header-toggle .v-input__slot {
      width: 48px;
    }
  }

  @media screen and (max-width: 993px) {
    .header-text {
      display: none;
    }
  }

  @media only screen and (max-width: 1660px) {
    .v-toolbar__content {
      padding: 0 16px;
    }

    .header-buttons {
      .border-single {
        min-width: 90px !important;
      }
    }

    .d-block .v-toolbar__content {
      display: block !important;
    }

    .navbar-icon {
      display: none;
    }

    .header-coins {
      justify-content: flex-start;
    }

    .header-coins::before {
      display: none;
    }

    .header-coins span {
      font-size: 20px;
      max-width: none;
    }

    .v-application .header-coins.mr-4 {
      margin-right: 0 !important;
    }

    .availablePackagesMobile .carousel-3d-slide.current .v-image::before {
      background-image: url('/images/icons/selected-package-frame.png');
      background-position: center center;
      background-size: contain;
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
    }

    .availablePackagesMobile .carousel-3d-slide .v-image__image {
      transform: scale(0.9);
    }

    .availablePackagesMobile .carousel-3d-slide .card-top-content {
      top: 10%;
    }

    .availablePackagesMobile .carousel-3d-slide .card-bottom-content {
      bottom: 11%;
    }
  }

  @media screen and (min-width: 993px) {
    .mobile-header-row-2 {
      justify-content: center;
    }

    .navbar-icon-mobile.is-login {
      display: none;
    }
  }

  @media screen and (max-width: 768px) {
    .navbar-icon-mobile.is-login {
      display: none;
    }
  }

  @media only screen and (max-width: 768px) {
    /***   ALERT DIALOG   ***/
    .alert-dialog {
      top: auto;
      bottom: 44px;
    }
    .alert-dialog::after {
      bottom: -8px;
      top: auto;
      left: 196px;
      border-top: 8px solid #422558;
      border-bottom: none;
    }
    /************************/

    .header-buttons.is-login.d-flex {
      justify-content: space-between;
      flex: 1;
    }

    .header-row-2-logo {
      background-position: center;
      margin: 0 auto !important;

      @media screen and (max-width: 600px) {
        .header-logo {
          margin: 0 auto !important;
        }
      }
    }

    .header-row-1 {
      justify-content: space-between;
      display: flex;
    }

    .header-friends-button,
    .header-add-friends-button {
      display: none;
    }

    .v-badge__badge.red::after {
      border-color: #191919 !important;
    }

    .withdrawalSliderDesktop {
      display: none;
    }

    .withdrawalSliderMobile {
      display: block;
    }

    .withdrawalSliderMobile .carousel-3d-slide.current::before {
      background-image: url('/images/icons/IconSelected_Glow.png');
      background-position: center center;
      background-size: contain;
      content: '';
      position: absolute;
      width: 90%;
      height: 90%;
      top: 5%;
      left: 5%;
    }
  }

  @media only screen and (max-width: 640px) {
    .mobile-header-row-2 {
      font-size: 16px;
    }

    .walletTabs .tab_section {
      padding: 20px 0;
    }
  }

  @container header (width < 820px) {
    .header-buttons {
      .border-single {
        min-width: 0 !important;
      }
    }

    .header-coins-body {
      max-width: 150px !important;

      .v-input--switch__track {
        left: 18px;
        width: 25px;
      }

      .balance-coin {
        width: 20px !important;
        height: 20px !important;
        left: 4px !important;
      }

      .coins-amount {
        left: 30px !important;
        max-width: 80px !important;
      }
    }

    .header-toggle .v-input--switch__track {
      background-color: white;
      opacity: 1 !important;
      height: 10px;
      margin-top: 2px;
    }

    .header-toggle .v-input--switch__thumb {
      width: 15px;
      left: 13px;
      height: 15px;
      margin-top: 2px;
    }

    .header-coins {
      span {
        font-size: 16px;
        max-width: 60px;
        text-align: center;
      }

      .header-coins-icon {
        left: 6px;

        &.MMC {
          right: -2px;
        }
      }
    }
  }

  @container header (width < 600px) {
    .header-toggle .v-input--switch__track {
      background-color: white;
      opacity: 1 !important;
      height: 10px;
      margin-top: 2px;
      left: 10px;
    }

    .header-toggle .v-input--switch__thumb {
      width: 15px;
      left: 5px;
      height: 15px;
      margin-top: 2px;
    }
  }

  @media only screen and (max-width: 600px) {
    .walletDialog {
      position: relative;
      z-index: 1;
      box-sizing: border-box;
      background-image: url('/images/backgrounds/wallet_bgr_mobile.png');
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      display: grid;
      grid-template-rows: 0.6fr 5fr;
    }

    .walletTabs {
      display: grid;
      grid-template-rows: 0.6fr 5fr;
    }

    .walletTabs .v-tabs-items {
      align-self: center;
    }

    .walletTabs .v-slide-group__prev.v-slide-group__prev--disabled {
      display: none !important;
    }

    .walletTabs.theme--dark.v-tabs > .v-tabs-bar {
      background-color: transparent !important;
      height: auto !important;
    }

    .walletDialog .v-tabs-items,
    .walletDialog .tab_section {
      background-color: transparent;
    }

    .walletTabs .walletMobileTabs {
      display: grid;
      grid-template-columns: 45% 45%;
      margin-top: 20px;
      justify-content: center;
      margin-bottom: 20px;
      width: 100%;
      gap: 15px;
    }

    .walletMobileTabs .wallet-tab {
      font-size: 20px;
      padding: 10px;
      height: fit-content;
    }

    .walletMobileTabs .wallet-tab:not(.v-tab--active):hover {
      filter: brightness(0.8);
    }

    .walletMobileTabs .v-ripple__container {
      display: none;
    }

    .walletMobileTabs .wallet-tab.left {
      border-top-left-radius: 10px;
      width: 80%;
      border-bottom-left-radius: 10px;
      margin-left: 0;
    }

    .walletMobileTabs .wallet-tab.left.first {
      justify-self: right;
      width: 80%;
      margin-right: 12px;
    }

    .walletMobileTabs .wallet-tab.right {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
      margin-left: -6%;
      width: 100%;
    }

    .walletMobileTabs .wallet-tab.right.first {
      justify-self: left;
      width: 80%;
      margin-left: 14px;
    }

    .walletMobileTabs .wallet-tab.left::after {
      content: '';
      line-height: 0;
      font-size: 0;
      width: 0;
      height: 0;
      border-top: 40px solid #8779b6;
      border-bottom: 0 solid transparent;
      border-left: 0 solid transparent;
      border-right: 30px solid transparent;
      position: absolute;
      top: 0;
      right: -29px;
      background: none;
      bottom: initial;
      left: initial;
    }

    .walletMobileTabs .wallet-tab.left.second::after {
      border-top: 40px solid #8779b6;
      border-bottom: 0;
      border-left: 0;
      border-right: 30px solid transparent;
      top: 0;
      right: -29px;
    }

    .walletMobileTabs .wallet-tab.right::after {
      content: '';
      line-height: 0;
      font-size: 0;
      width: 0;
      height: 0;
      border-top: 50px solid transparent;
      border-bottom: 40px solid #8779b6;
      border-left: 30px solid transparent;
      border-right: 0;
      position: absolute;
      top: -50px;
      left: -29px;
      // transition: all 400ms ease;
    }

    .walletMobileTabs .wallet-tab.right.second::after {
      border-top: 50px solid transparent;
      border-bottom: 40px solid #8779b6;
      border-left: 30px solid transparent;
      border-right: 0 solid #8779b6;
      top: -50px;
      left: -29px;
    }

    .walletMobileTabs .wallet-tab.left.v-tab--active::after {
      border-top: 41px solid #432559;
    }

    .walletMobileTabs .wallet-tab.right.v-tab--active::after {
      border-bottom: 41px solid #432559;
      border-right: 0;
    }

    .walletTabs .tab_section {
      padding-bottom: 100px !important;
    }
  }

  @media screen and (min-width: 768px) {
    .header-buttons {
      .challenge-friends-button {
        min-height: 36px !important;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .challenge-friends-button {
      min-width: 160px !important;
      min-height: 30px !important;
    }

    .online-friends {
      padding-left: 0 !important;
      height: 33px !important;
      display: inline-block !important;
      background: var(--v-green-base);
      width: 33px !important;
      min-width: 0 !important;
      transform: translateY(-18px) !important;
      right: -16.3px !important;
    }
  }

  @media only screen and (max-width: 550px) {
    .header-logo.is-logout {
      width: 180px !important;
      max-width: 40%;
    }

    .mobile-header-row-2 {
      font-size: 14px;
    }
  }

  @media only screen and (max-width: 450px) {
    .header-coins span {
      font-size: 16px;
      max-width: 60px;
      text-align: center;
    }

    .header-toggle .v-input--selection-controls__input {
      width: 48px !important;
    }

    .header-toggle .v-input__slot {
      width: 48px;
    }

    .header-buttons .row.mx-4 {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .walletMobileTabs .text-h6 {
      font-size: 15px !important;
    }

    .walletMobileTabs .wallet-tab.right::after {
      border-top: 50px solid transparent;
      border-bottom: 35px solid #8779b6;
    }

    .walletMobileTabs .wallet-tab.right::after {
      border-bottom: 35px solid #8779b6;
    }

    .walletMobileTabs .wallet-tab.left::after {
      border-top: 36px solid #8779b6;
    }

    .walletMobileTabs .wallet-tab.left.second::after {
      border-top: 36px solid #8779b6;
    }

    .walletMobileTabs .wallet-tab.right.second::after {
      border-bottom: 35px solid #8779b6;
    }

    .walletMobileTabs .wallet-tab.right.v-tab--active::after {
      border-bottom: 35px solid #432559;
    }

    .walletMobileTabs .wallet-tab.left.v-tab--active::after {
      border-top: 36px solid #432559;
    }
  }
</style>

<style lang="scss" scoped>
  @import 'src/scss/mixins';

  .promo-button::v-deep {
    padding: 0 !important;
    min-height: 36px !important;

    @media screen and (max-width: 768px) {
      width: 90px !important;
    }

    @media screen and (max-width: 400px) {
      width: 70px !important;
    }

    .v-btn__content {
      padding-bottom: 0 !important;

      img {
        filter: invert(1);
        @media screen and (max-width: 768px) {
          height: 15px;
        }

        @media screen and (max-width: 400px) {
          display: none;
        }
      }

      .button-text {
        font-size: 18px !important;
        display: flex;
        width: 100% !important;
        justify-content: center;
        position: relative;

        @media screen and (max-width: 768px) {
          height: 15px;
          font-size: 14px !important;
        }

        /*&:before {
      content: "";
      width: 1px;
      height: 100%;
      display: inline-block;
      background: white;
      position: absolute;
      left: 4px;

      @media screen and (max-width: 768px) {
        left: 2px;
      }

      @media screen and (max-width: 400px) {
        display: none;
      }
    }*/
      }
    }

    @media screen and (max-width: 768px) {
      min-height: 25px !important;
      height: 24px !important;
    }

    &:hover {
      .v-btn__content {
        img {
          filter: invert(0);
        }

        .button-text {
          &:before {
            background: black;
          }
        }
      }
    }
  }

  .challenge-friends-button {
    min-width: 130px !important;

    span {
      position: relative;

      div {
        img {
          filter: invert(1);
        }
      }

      .online-friends {
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        position: absolute;
        color: white;
        height: 36px;
        padding-top: 8px;
        display: inline-block;
        align-items: center;
        justify-content: center;
        background: #73b618;
        width: 40px;
        top: 50%;
        transform: translateY(-50%);
        right: -18px;
        border-bottom-right-radius: 15px;
        border-top-right-radius: 15px;
      }
    }

    &::v-deep .v-badge__badge {
      margin-left: 5px;
    }

    span {
      line-height: 12px;
    }

    &.bottom-bar {
      padding-top: 3px;
      margin: 0 !important;
    }

    .challenge-text {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        left: -3px;
        top: 0;
        height: 100%;
        width: 1px;
        background: white;
      }
    }

    &:hover {
      .v-btn__content {
        div {
          img {
            filter: invert(0);
          }

          .challenge-text {
            &:after {
              background: black;
            }
          }
        }
      }
    }

    @media screen and (max-width: 768px) {
      &.top-bar {
        display: none;
      }

      &::v-deep .v-badge__badge {
        inset: auto auto calc(100% - 15px) calc(100% - 15px) !important;
      }
    }
  }

  .v-tooltip__content {
    background: black;
    border-radius: 10px;

    &:before {
      content: '';
      position: absolute;
      height: 10px;
      width: 7px;
      background: black;
      top: -8px;
      transform: rotate(90deg);
      right: 25px;
      clip-path: polygon(100% 0, 0 50%, 100% 100%);
    }
  }

  .v-app-bar {
    background: #24073d !important;

    &--fixed {
      z-index: 9;
    }

    &--fixed {
      z-index: 9;
    }

    &::v-deep .v-toolbar__content {
      container: app-bar / inline-size;
      width: 100%;
      padding: 0;
    }
  }

  .promo-button {
    .promos-amount {
      position: absolute;
      top: -5px;
      left: -1px;
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #73b618;
      border-top-left-radius: 14px;
      border-bottom-left-radius: 14px;
      padding: 0 6px;

      @media screen and (min-width: 769px) {
        height: 36px;
        left: -2px;
        top: 50%;
        transform: translateY(-50%);
        padding: 0 12px;
      }
    }

    .button-text {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 14px;

      @media screen and (min-width: 769px) {
        margin-left: 28px;
      }
    }
  }

  .refer-now-mobile {
    min-width: 80px !important;
    min-height: 25px !important;
    height: 25px !important;
    padding: 0 8px !important;

    &::v-deep .v-btn__content {
      font-size: 14px !important;
    }

    @media screen and (max-width: 489px) {
      display: none;
    }

    @media screen and (min-width: 520px) {
      padding: 0 12px !important;

      &::v-deep .v-btn__content {
        font-size: 14px !important;
      }
    }

    @media screen and (min-width: 769px) {
      display: none;
    }

    @media screen and (min-width: 940px) {
      display: block;
      min-width: 110px !important;
      min-height: 36px !important;
      padding: 0 16px !important;

      &::v-deep .v-btn__content {
        font-size: 18px !important;
        line-height: 32px !important;
      }
    }

    @media screen and (min-width: 1661px) {
      display: none;
    }
  }
</style>
